import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Banknote, Bike, HandCoins, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  DeliveryListRequest,
  DeliveryRecap,
  DeliverySort,
  DeliverySummary,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { add, formatMoney, isPositive, ZERO_MONEY } from '@aeris/money'
import {
  formatDocumentNumber,
  reportPrintFor,
  type PrintDocument,
} from '@aeris/printing'
import {
  ActiveFilters,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  ListToolbar,
  MoneyInput,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  RadioGroup,
  ResultCount,
  SearchInput,
  Select,
  SortableHeaderCell,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  type ActiveFilterChip,
} from '@aeris/ui'

import { AuthorisationDialog } from '../components/authorisation-dialog.tsx'
import { DeliverySettleRow } from '../components/delivery-settle-row.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { LoadFailed } from '../components/load-failed.tsx'
import { PartyPicker } from '../components/party-picker.tsx'
import { PeriodPicker } from '../components/period-picker.tsx'
import { PrintDocumentDialog } from '../components/print-document-dialog.tsx'
import { TerminalSelect } from '../components/terminal-select.tsx'
import { recordExpense } from '../lib/api/expenses.ts'
import {
  ApiError,
  fetchCurrentCashSession,
  fetchDeliveries,
  fetchPaymentMethods,
  settleFinanceEntrySplit,
  transitionSalesDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { DELIVERY_EXPORT, paidWith, settledBy } from '../lib/delivery-export.ts'
import {
  customRange,
  exclusiveEnd,
  PERIOD_LABELS,
  periodRange,
  type PeriodChoice,
} from '../lib/period-range.ts'
import {
  readRememberedTerminal,
  rememberTerminal,
} from '../lib/remembered-terminal.ts'
import { streetMethods, type SettlePart } from '../lib/street-methods.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'
import { useTableSort } from '../lib/use-table-sort.ts'

/**
 * O acerto do motoboy — a tela do caixa (F034, refeita na F161).
 *
 * O dono, na F161: *"o caixa vai precisar ter uma página ultra rápida pra
 * motoboy: quando despachar e escolher o motoboy, vai saber quanto tem pra
 * acertar com cada motoboy no dia, quanto ele tem que dar de dinheiro — ele
 * leva o pedido, o cliente paga ele e ele dá o dinheiro pro caixa —, aí o caixa
 * tem que marcar os que já acertou"*.
 *
 * A ordem da tela é a ordem do balcão:
 *
 *   1. **quem voltou** — um cartão por motoboy, com quanto ele tem que trazer.
 *      Sem digitar nome: o caixa bate o olho e clica;
 *   2. **como cada cliente pagou** — em cada entrega, a forma num clique, ou
 *      "Dividir" quando foi parte dinheiro e parte cartão;
 *   3. **o que já foi acertado** — a aba Acertados, por dia, com as formas.
 *
 * O dinheiro em espécie entra no caixa aberto deste terminal (ADR-0021,
 * decisão 9): a cédula está fisicamente na gaveta.
 */

const money = (wire: string) =>
  wire.trim() === '' ? ZERO_MONEY : parseAmount(wire)

const stamp = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

const localDay = (at: Date): string => {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}

const PAGE_SIZE = 25
const EXPORT_PAGE_SIZE = 200
/** O papel lê tudo o que o filtro achou; este é o limite de segurança (F188). */
const PRINT_PAGE_CAP = 50

/**
 * Todas as entregas do filtro, de 200 em 200 — o papel e o "Tudo em
 * dinheiro" leem o filtro inteiro, nunca só a página na tela (F188, F248).
 */
const fetchEveryDelivery = async (
  request: DeliveryListRequest,
  signal?: AbortSignal
): Promise<{ items: DeliverySummary[]; total: number }> => {
  const items: DeliverySummary[] = []
  let total = 0
  for (let page = 1; page <= PRINT_PAGE_CAP; page += 1) {
    const response = await fetchDeliveries(
      { ...request, page, pageSize: EXPORT_PAGE_SIZE },
      signal
    )
    total = response.total
    items.push(...response.items)
    if (response.items.length === 0 || items.length >= response.total) break
  }
  return { items, total }
}

/**
 * Quanto tempo a mercadoria ficou (ou está) na rua — F189.
 *
 * Em aberto conta até agora, de propósito: a entrega que não voltou é a que
 * está demorando, e um traço ali esconderia justamente a que o caixa precisa
 * cobrar.
 */
const streetTime = (delivery: DeliverySummary): string => {
  if (delivery.dispatchedAt === null) return '—'
  const from = new Date(delivery.dispatchedAt).getTime()
  const to =
    delivery.settledAt === null
      ? Date.now()
      : new Date(delivery.settledAt).getTime()
  const minutes = Math.max(0, Math.round((to - from) / 60_000))
  if (minutes < 60) return `${String(minutes)} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${String(hours)} h`
  const days = Math.floor(hours / 24)
  return `${String(days)} ${days === 1 ? 'dia' : 'dias'}`
}

/**
 * A entrega paga pela metade — e o crachá que nunca acendeu.
 *
 * `delivery-settle-row.tsx` testava `entryStatus === 'partial'` desde a F161,
 * e `finance_entry` só admite `open`, `settled` e `cancelled` (0019): o estado
 * não existe e o crachá jamais apareceu. A pergunta certa não é o status do
 * título — é se **já entrou dinheiro e ainda falta**.
 */
const isPartlyPaid = (delivery: DeliverySummary): boolean =>
  delivery.settlements.length > 0 && isPositive(money(delivery.outstanding))

export const EntregasPage = (): ReactNode => {
  const { allows, session: authSession } = useAuth()
  const canSettle = allows('finance.settle')
  const canPayCourier = allows('expense.write')
  const queryClient = useQueryClient()

  /** '' é "todos". */
  const [courierPartyId, setCourierPartyId] = useState('')
  const [tab, setTab] = useState<'open' | 'settled'>('open')
  const [period, setPeriod] = useState<PeriodChoice>('today')
  /**
   * "Qualquer data" — F203. O período agora vale nas duas abas, mas abre sem
   * recorte: a entrega de ontem que ninguém acertou ainda é dívida de hoje.
   */
  const [anyDate, setAnyDate] = useState(true)
  /**
   * Sem ninguém mexer, cada aba abre no seu período: Em aberto sem data,
   * Acertados em "Hoje" — o maço do dia. Depois que a pessoa escolhe, a escolha
   * vale nas duas abas.
   */
  const [periodTouched, setPeriodTouched] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [returning, setReturning] = useState<DeliverySummary | null>(null)
  const [paying, setPaying] = useState(false)
  const [cashOnly, setCashOnly] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /** O recorte da lista — F189. */
  const [search, setSearch] = useState('')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [page, setPage] = useState(1)
  const sort = useTableSort<DeliverySort>('dispatchedAt', 'desc', () => {
    setPage(1)
  })
  const debouncedSearch = useDebounced(search)
  const [printable, setPrintable] = useState<PrintDocument | null>(null)

  const branchId = authSession?.context?.branchId ?? undefined

  /*
   * O caixa desta máquina, como ESTADO (F099): escolher o terminal aqui refaz
   * a consulta na hora. Cartão e PIX funcionam sem caixa; só o dinheiro pede.
   */
  const [terminalId, setTerminalId] = useState(readRememberedTerminal)

  const till = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: terminalId !== '',
  })
  const cashSessionId = till.data?.session?.id ?? null

  const range = anyDate
    ? null
    : period === 'custom'
      ? customRange(customFrom, customTo)
      : periodRange(period)

  /*
   * O recorte, montado uma vez e usado três — F189.
   *
   * A lista, a planilha e o papel perguntam a MESMA coisa, com páginas
   * diferentes. Montar cada um por conta própria é como a exportação de uma
   * tela acaba levando mais do que a tela mostra.
   */
  const listRequest: DeliveryListRequest = {
    ...(courierPartyId === '' ? {} : { courierPartyId }),
    scope: tab,
    ...(range === null ? {} : { from: range.from, to: exclusiveEnd(range.to) }),
    ...(debouncedSearch.trim() === '' ? {} : { search: debouncedSearch }),
    ...(paymentMethodId === '' ? {} : { paymentMethodId }),
    sort: sort.sort,
    direction: sort.direction,
  }

  const deliveries = useQuery({
    queryKey: ['deliveries', listRequest, page],
    queryFn: ({ signal }) =>
      fetchDeliveries({ ...listRequest, page, pageSize: PAGE_SIZE }, signal),
    // Trocar de motoboy não apaga os cartões enquanto a lista nova chega: o
    // caixa clicaria num cartão que some debaixo do dedo.
    placeholderData: keepPreviousData,
  })

  /*
   * "Tudo em dinheiro" acerta TODAS as entregas abertas do filtro — F248.
   *
   * O diálogo mostrava a soma do filtro inteiro e a baixa corria só nas 25 da
   * página: com 30 na rua, o caixa conferia R$ 300 no maço, confirmava, e 5
   * ficavam abertas — o caixa fechava com sobra. Agora a mesma lista dá a
   * contagem, a soma e a baixa.
   */
  const everyOpen = useQuery({
    queryKey: ['deliveries', 'all', listRequest],
    queryFn: async ({ signal }) =>
      await fetchEveryDelivery(listRequest, signal),
    enabled: cashOnly,
    staleTime: 0,
    gcTime: 0,
  })
  const settleable = (everyOpen.data?.items ?? []).filter(
    (delivery) => delivery.entryId !== null && delivery.entryVersion !== null
  )
  const settleableTotal = settleable.reduce(
    (sum, delivery) => add(sum, money(delivery.outstanding)),
    ZERO_MONEY
  )
  const tooMany =
    everyOpen.data !== undefined &&
    everyOpen.data.items.length < everyOpen.data.total

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: canSettle,
  })
  const street = streetMethods(methods.data?.methods ?? [])
  const cashMethod = street.find((method) => method.kind === 'cash')

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['deliveries'] })
    void queryClient.invalidateQueries({ queryKey: ['finance'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
  }

  const failure = (cause: unknown, fallback: string): string =>
    cause instanceof ApiError ? cause.message : fallback

  /**
   * F293: o "deixa na conta" recusado por título vencido. A tela pede a senha
   * do gerente e manda o mesmo acerto de novo, com a liberação.
   */
  const [releasing, setReleasing] = useState<{
    delivery: DeliverySummary
    parts: readonly SettlePart[]
  } | null>(null)

  const releaseParty = releasing?.delivery.customerPartyId ?? null

  /** Uma entrega recebida, numa forma ou dividida — sempre numa transação. */
  const settle = useMutation({
    mutationFn: async (input: {
      delivery: DeliverySummary
      parts: readonly SettlePart[]
      creditAuthorisationId?: string | null
    }) => {
      if (
        input.delivery.entryId === null ||
        input.delivery.entryVersion === null
      )
        throw new Error('Esta entrega não tem título a receber.')
      await settleFinanceEntrySplit(input.delivery.entryId, {
        version: input.delivery.entryVersion,
        branchId: branchId ?? '',
        parts: [...input.parts],
        cashSessionId,
        creditAuthorisationId: input.creditAuthorisationId ?? null,
        note: null,
      })
      return input.delivery
    },
    onSuccess: async (delivery) => {
      setError(null)
      setNotice(
        `Pedido nº ${formatDocumentNumber(delivery.number, '')} de ${delivery.customerName} acertado.`
      )
      await refresh()
    },
    onError: (cause: unknown, input) => {
      setNotice(null)
      setError(failure(cause, 'Não foi possível registrar o recebimento.'))
      if (
        cause instanceof ApiError &&
        cause.code === 'credit_overdue' &&
        input.delivery.customerPartyId !== null &&
        (input.creditAuthorisationId ?? null) === null
      ) {
        setReleasing({ delivery: input.delivery, parts: input.parts })
      }
    },
  })

  /**
   * "Tudo em dinheiro": o motoboy entregou o maço e disse que todos pagaram em
   * espécie. Uma baixa por entrega, uma depois da outra; se uma falhar, as que
   * já entraram ficam (cada uma é uma transação inteira) e a tela diz qual
   * parou.
   */
  const settleAllCash = useMutation({
    mutationFn: async (list: readonly DeliverySummary[]) => {
      if (cashMethod === undefined) throw new Error('Sem forma "Dinheiro".')
      let done = 0
      for (const delivery of list) {
        if (delivery.entryId === null || delivery.entryVersion === null)
          continue
        await settleFinanceEntrySplit(delivery.entryId, {
          version: delivery.entryVersion,
          branchId: branchId ?? '',
          parts: [
            { paymentMethodId: cashMethod.id, amount: delivery.outstanding },
          ],
          cashSessionId,
          creditAuthorisationId: null,
          note: null,
        })
        done += 1
      }
      return done
    },
    onSuccess: async (done) => {
      setCashOnly(false)
      setError(null)
      setNotice(
        `${String(done)} ${done === 1 ? 'entrega acertada' : 'entregas acertadas'} em dinheiro.`
      )
      await refresh()
    },
    onError: async (cause: unknown) => {
      setCashOnly(false)
      setNotice(null)
      setError(failure(cause, 'Parou no meio: confira a lista.'))
      await refresh()
    },
  })

  /** Desfazer a entrega: a peça volta ao estoque e o título morre (F159). */
  const undo = useMutation({
    mutationFn: async (input: {
      documentId: string
      documentVersion: number
      authorisationId: string
      reason: string
    }) =>
      await transitionSalesDocument(input.documentId, {
        version: input.documentVersion,
        to: 'cancelled',
        reason: input.reason,
        authorisationId: input.authorisationId,
      }),
    onSuccess: async (response) => {
      setError(null)
      setReturning(null)
      setNotice(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} desfeito. A mercadoria voltou ao estoque e o título foi cancelado.`
      )
      await refresh()
      void queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
    onError: (cause: unknown) => {
      setError(failure(cause, 'Não foi possível desfazer a entrega.'))
    },
  })

  const items = deliveries.data?.items ?? []
  const couriers = deliveries.data?.couriers ?? []
  const selected = couriers.find((entry) => entry.partyId === courierPartyId)
  const streetTotal = couriers.reduce((sum, entry) => sum + entry.openCount, 0)
  const selectedName =
    selected?.name ?? deliveries.data?.courierName ?? 'o motoboy'

  const total = deliveries.data?.total ?? 0
  const filtering = debouncedSearch.trim() !== '' || paymentMethodId !== ''

  const clearFilters = (): void => {
    setSearch('')
    setPaymentMethodId('')
    setPage(1)
  }

  const methodName = (id: string): string =>
    (methods.data?.methods ?? []).find((entry) => entry.id === id)?.name ?? '—'

  const chips: ActiveFilterChip[] = [
    ...(debouncedSearch.trim() === ''
      ? []
      : [
          {
            id: 'search',
            label: `Busca: ${debouncedSearch.trim()}`,
            onRemove: () => {
              setSearch('')
              setPage(1)
            },
          },
        ]),
    ...(paymentMethodId === ''
      ? []
      : [
          {
            id: 'method',
            label: `Forma: ${methodName(paymentMethodId)}`,
            onRemove: () => {
              setPaymentMethodId('')
              setPage(1)
            },
          },
        ]),
  ]

  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  /*
   * O acerto no papel — F189.
   *
   * O caixa fecha o dia e entrega a folha para o dono conferir contra o maço.
   * Lê TUDO o que o filtro achou, não a página: o dia do motoboy tem 40
   * entregas, e a página tem 25. É o mesmo `reportPrintFor` de Relatórios, de
   * Comissões e do relatório do Eduardo, com o cabeçalho da loja e o recorte
   * escrito no topo — senão o papel não diz de que dia ele é.
   */
  const printSettlement = useMutation({
    mutationFn: async () => {
      const { items: collected } = await fetchEveryDelivery(listRequest)

      return reportPrintFor({
        company,
        template,
        title: tab === 'open' ? 'Entregas na rua' : 'Acerto do motoboy',
        filters: [
          {
            label: 'Motoboy',
            value: courierPartyId === '' ? 'Todos' : selectedName,
          },
          {
            label: 'Período',
            value:
              tab === 'open'
                ? 'Em aberto'
                : period === 'custom'
                  ? `${customFrom} a ${customTo}`
                  : PERIOD_LABELS[period],
          },
          {
            label: 'Forma',
            value:
              paymentMethodId === '' ? 'Todas' : methodName(paymentMethodId),
          },
          { label: 'Entregas', value: String(collected.length) },
        ],
        columns: [
          { label: 'Como pagou' },
          { label: 'Cliente' },
          { label: 'Bairro' },
          { label: 'Motoboy' },
          { label: 'Quem acertou' },
          { label: 'Total' },
        ],
        rows: collected.map((row) => ({
          label: formatDocumentNumber(row.number),
          values: [
            row.settlements.length === 0 ? 'Não acertou' : paidWith(row),
            row.customerName,
            row.customerDistrict ?? '—',
            row.courierName ?? '—',
            settledBy(row) === '' ? '—' : settledBy(row),
            formatMoney(money(row.totalAmount)),
          ],
        })),
        others: null,
        /*
         * Só o primeiro valor: `reportPrintFor` escreve os demais rotulados
         * embaixo, e strings vazias sairiam como "Cliente: · Bairro:" sem nada
         * depois dos dois pontos (o achado da F188).
         */
        total: {
          label: tab === 'open' ? 'Falta trazer' : 'Total acertado',
          values: [
            formatMoney(
              collected.reduce(
                (sum, row) =>
                  add(
                    sum,
                    money(tab === 'open' ? row.outstanding : row.totalAmount)
                  ),
                ZERO_MONEY
              )
            ),
          ],
        },
      })
    },
    onSuccess: setPrintable,
  })

  return (
    <Page>
      <PageHeader
        title="Acerto do motoboy"
        description="Quem está na rua, quanto cada um tem que trazer, e como cada cliente pagou."
      />

      {terminalId === '' && canSettle ? (
        <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-3">
          <p className="text-(length:--text-xs) text-(--text-secondary)">
            Este computador ainda não disse em qual caixa está. Escolha para que
            o dinheiro entre na gaveta certa — cartão e PIX não precisam.
          </p>
          <TerminalSelect
            value={terminalId}
            onValueChange={(code) => {
              rememberTerminal(code)
              setTerminalId(code)
            }}
          />
        </div>
      ) : null}

      {/*
        O filtro vem ANTES — F203. Com 300 pedidos no dia, escolher o motoboy
        depois de a tabela carregar não serve: o motoboy e o período mandam em
        tudo o que vem abaixo, nas duas abas.
      */}
      <div className="flex flex-wrap items-end gap-3 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface) p-3">
        <Field label="Motoboy" className="w-full sm:w-72">
          {(field) => (
            <PartyPicker
              {...field}
              role="courier"
              value={courierPartyId}
              placeholder="Todos os motoboys"
              onChange={(partyId) => {
                setCourierPartyId(partyId)
                setPage(1)
              }}
            />
          )}
        </Field>
        <PeriodPicker
          choice={period}
          onChoiceChange={(choice) => {
            setPeriodTouched(true)
            setAnyDate(false)
            setPeriod(choice)
            setPage(1)
          }}
          anyDate={{
            selected: anyDate,
            onSelect: () => {
              setPeriodTouched(true)
              setAnyDate(true)
              setPage(1)
            },
          }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
      </div>

      <DeliveryRecapTiles
        recap={deliveries.data?.recap}
        loading={deliveries.isPending}
        error={deliveries.isError}
        who={courierPartyId === '' ? 'Todos os motoboys' : selectedName}
      />

      {/*
        Quem deve agora: um toque escolhe o motoboy. Sem recorte de data — o
        que está na rua é dívida de qualquer dia (F161).
      */}
      <div role="group" aria-label="Motoboys" className="flex flex-wrap gap-2">
        <CourierCard
          name="Todos"
          detail={`${String(streetTotal)} na rua`}
          amount={couriers.reduce(
            (sum, entry) => add(sum, money(entry.toCollect)),
            ZERO_MONEY
          )}
          pressed={courierPartyId === ''}
          onClick={() => {
            setCourierPartyId('')
          }}
        />
        {couriers.map((entry) => (
          <CourierCard
            key={entry.partyId}
            name={entry.name}
            detail={`${String(entry.openCount)} ${entry.openCount === 1 ? 'entrega' : 'entregas'}`}
            amount={money(entry.toCollect)}
            pressed={courierPartyId === entry.partyId}
            onClick={() => {
              setCourierPartyId(entry.partyId)
            }}
          />
        ))}
      </div>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}
      {notice === null ? null : (
        <InlineAlert tone="success">{notice}</InlineAlert>
      )}

      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as 'open' | 'settled')
          if (!periodTouched) setAnyDate(value === 'open')
        }}
      >
        <div className="flex flex-wrap items-end justify-between gap-2">
          <TabsList aria-label="Entregas">
            <TabsTrigger value="open">Em aberto</TabsTrigger>
            <TabsTrigger value="settled">Acertados</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap gap-2">
            {/*
              O papel do fim do dia — F189. Em aberto ele é a lista do que
              está na rua; em Acertados, a conferência contra o maço.
            */}
            <Button
              variant="secondary"
              loading={printSettlement.isPending}
              disabled={total === 0}
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                printSettlement.mutate()
              }}
            >
              Imprimir
            </Button>
            <ExportMenu
              fileName={`acerto-${tab === 'open' ? 'em-aberto' : 'acertados'}`}
              sheetName="Entregas"
              columns={DELIVERY_EXPORT}
              /*
                O arquivo leva o RECORTE que está na tela, e pagina pela mesma
                rota da lista — então a permissão é a mesma, e não há porta por
                onde saia mais do que a tela mostra.
              */
              fetchPage={async (wanted) => {
                const response = await fetchDeliveries({
                  ...listRequest,
                  page: wanted,
                  pageSize: EXPORT_PAGE_SIZE,
                })
                return { items: response.items, total: response.total }
              }}
              pageSize={EXPORT_PAGE_SIZE}
              total={total}
            />
            {tab === 'open' &&
            courierPartyId !== '' &&
            canSettle &&
            cashMethod !== undefined &&
            items.length > 0 ? (
              <Button
                variant="secondary"
                leadingIcon={
                  <Banknote aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setCashOnly(true)
                }}
              >
                Tudo em dinheiro
              </Button>
            ) : null}
            {courierPartyId !== '' && canPayCourier ? (
              <Button
                variant="secondary"
                leadingIcon={
                  <HandCoins aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setPaying(true)
                }}
              >
                Pagar o motoboy
              </Button>
            ) : null}
          </div>
        </div>

        {/*
          A barra que as outras trinta listas já tinham — F189.

          O dono: *"tudo muito seco"*. A tela não tinha busca, filtro,
          ordenação, paginação nem exportar: ela pedia 200 entregas de uma vez
          e desenhava todas. Os cartões por motoboy ficam onde estavam — é o
          gesto do caixa, e é a melhor parte da tela.
        */}
        <div className="pt-3">
          <ListToolbar>
            <SearchInput
              aria-label="Buscar entrega"
              placeholder="Nº do pedido ou cliente…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
            />
            {tab === 'settled' ? (
              <Field label="Forma de pagamento">
                {(field) => (
                  <Select
                    {...field}
                    value={paymentMethodId}
                    onChange={(event) => {
                      setPaymentMethodId(event.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Todas as formas</option>
                    {(methods.data?.methods ?? []).map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : null}
          </ListToolbar>

          <ActiveFilters chips={chips} onClearAll={clearFilters} />

          <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2">
            <ResultCount
              page={deliveries.data?.page ?? page}
              pageSize={PAGE_SIZE}
              total={total}
              loading={deliveries.isPending}
              none="Nenhuma entrega com esse filtro"
            />
            {deliveries.data === undefined ? null : (
              <p className="text-(length:--text-xs) text-(--text-secondary)">
                {tab === 'open' ? 'Falta trazer: ' : 'A receber no recorte: '}
                <strong className="font-(--font-mono) tabular-nums">
                  {formatMoney(money(deliveries.data.outstandingTotal))}
                </strong>
              </p>
            )}
          </div>
        </div>

        <TabsContent value="open" className="flex flex-col gap-3 pt-3">
          {deliveries.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : deliveries.isError ? (
            <LoadFailed
              what="as entregas em aberto"
              onRetry={() => {
                void deliveries.refetch()
              }}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bike aria-hidden="true" className="h-6 w-6" />}
              title={filtering ? 'Nada com esse filtro' : 'Nada em aberto'}
              description={
                filtering
                  ? 'Nenhuma entrega em aberto casa com o que você procurou.'
                  : courierPartyId === ''
                    ? 'Nenhuma entrega esperando acerto.'
                    : `${selectedName} não tem nada para acertar.`
              }
              action={
                filtering ? (
                  <Button variant="secondary" onClick={clearFilters}>
                    Limpar filtros
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                {courierPartyId === '' ? (
                  <>
                    Na rua:{' '}
                    <strong className="font-(--font-mono) text-(--text-primary)">
                      {formatMoney(money(deliveries.data.outstandingTotal))}
                    </strong>{' '}
                    em {total} {total === 1 ? 'entrega' : 'entregas'}.
                  </>
                ) : (
                  <>
                    <strong className="text-(--text-primary)">
                      {selectedName}
                    </strong>{' '}
                    tem que trazer{' '}
                    <strong className="font-(--font-mono) text-(--text-primary)">
                      {formatMoney(money(deliveries.data.outstandingTotal))}
                    </strong>
                    .
                  </>
                )}
              </p>
              <ul className="flex flex-col gap-2" aria-label="Em aberto">
                {items.map((delivery) => (
                  <DeliverySettleRow
                    key={delivery.documentId}
                    delivery={delivery}
                    /*
                      Todas as formas ativas, e não só as da rua (F179): a
                      linha decide quais oferecer, porque só ela sabe o saldo
                      e o limite daquele cliente.
                    */
                    methods={methods.data?.methods ?? []}
                    showCourier={courierPartyId === ''}
                    canSettle={canSettle}
                    busy={
                      settle.isPending &&
                      settle.variables.delivery.documentId ===
                        delivery.documentId
                    }
                    onSettle={(parts) => {
                      setNotice(null)
                      settle.mutate({ delivery, parts })
                    }}
                    onReturned={() => {
                      setReturning(delivery)
                    }}
                  />
                ))}
              </ul>
            </>
          )}
          <Pagination
            page={deliveries.data?.page ?? page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            label="Páginas das entregas na rua"
          />
        </TabsContent>

        <TabsContent value="settled" className="flex flex-col gap-3 pt-3">
          <QueryState
            isPending={deliveries.isPending}
            isError={deliveries.isError}
            error={deliveries.error}
            onRetry={() => {
              void deliveries.refetch()
            }}
            what="as entregas acertadas"
            isEmpty={items.length === 0}
            filtered={filtering}
            onClearFilters={clearFilters}
            empty={
              <EmptyState
                icon={<Banknote aria-hidden="true" className="h-6 w-6" />}
                title="Nada acertado"
                description="Nenhuma entrega que saiu neste período foi acertada ainda."
              />
            }
          >
            {/*
              No celular, cartões — F189.

              O caixa confere o acerto pelo tablet do balcão, e uma tabela de
              onze colunas ali é rolagem horizontal atrás de cada número.
            */}
            <ul
              className="flex flex-col gap-2 md:hidden"
              aria-label="Acertados"
            >
              {items.map((delivery) => (
                <li
                  key={delivery.documentId}
                  className="flex flex-col gap-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) px-3 py-2"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-(--font-mono) font-(--weight-semibold)">
                      {formatDocumentNumber(delivery.number)}
                    </span>
                    <span className="font-(--font-mono) tabular-nums">
                      {formatMoney(money(delivery.totalAmount))}
                    </span>
                  </div>
                  <span>{delivery.customerName}</span>
                  <span className="text-(length:--text-xs) text-(--text-muted)">
                    {delivery.customerDistrict ?? 'sem bairro'}
                    {delivery.courierName === null
                      ? ''
                      : ` · ${delivery.courierName}`}
                    {` · ${streetTime(delivery)} na rua`}
                  </span>
                  <span className="text-(length:--text-sm)">
                    {delivery.settlements.length === 0
                      ? 'Sem baixa'
                      : paidWith(delivery)}
                  </span>
                  <span className="text-(length:--text-xs) text-(--text-muted)">
                    {delivery.settledAt === null
                      ? 'não acertado'
                      : stamp.format(new Date(delivery.settledAt))}
                    {settledBy(delivery) === ''
                      ? ''
                      : ` · ${settledBy(delivery)}`}
                  </span>
                </li>
              ))}
            </ul>

            <TableContainer className="hidden md:block">
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeaderCell
                      numeric
                      className="w-20"
                      {...sort.column('number')}
                    >
                      Nº
                    </SortableHeaderCell>
                    <SortableHeaderCell {...sort.column('customer')}>
                      Cliente
                    </SortableHeaderCell>
                    <TableHeaderCell>Bairro</TableHeaderCell>
                    {courierPartyId === '' ? (
                      <SortableHeaderCell {...sort.column('courier')}>
                        Motoboy
                      </SortableHeaderCell>
                    ) : null}
                    <SortableHeaderCell
                      kind="date"
                      {...sort.column('dispatchedAt')}
                    >
                      Saiu
                    </SortableHeaderCell>
                    <SortableHeaderCell
                      kind="date"
                      {...sort.column('settledAt')}
                    >
                      Acertado
                    </SortableHeaderCell>
                    <SortableHeaderCell numeric {...sort.column('street')}>
                      Na rua
                    </SortableHeaderCell>
                    <TableHeaderCell>Combinou</TableHeaderCell>
                    <TableHeaderCell>Como pagou</TableHeaderCell>
                    <TableHeaderCell>Quem acertou</TableHeaderCell>
                    <SortableHeaderCell
                      numeric
                      className="w-28 text-right"
                      {...sort.column('total')}
                    >
                      Total
                    </SortableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((delivery) => (
                    <TableRow key={delivery.documentId}>
                      <TableCell className="font-(--font-mono) font-(--weight-semibold)">
                        {formatDocumentNumber(delivery.number)}
                      </TableCell>
                      <TableCell>
                        <span className="flex flex-wrap items-center gap-2">
                          {delivery.customerName}
                          {isPartlyPaid(delivery) ? (
                            <Badge tone="warning">parcial</Badge>
                          ) : null}
                          {/*
                            O estorno deixa de ser invisível — F189. São 812
                            no banco, e a tela nunca disse que houve algum:
                            a baixa estornada some de "Como pagou" (é dinheiro
                            que voltou), e some junto a informação de que
                            alguém desfez um acerto.
                          */}
                          {delivery.reversedCount === 0 ? null : (
                            <Badge tone="danger">
                              {delivery.reversedCount === 1
                                ? '1 estorno'
                                : `${String(delivery.reversedCount)} estornos`}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {delivery.customerDistrict ?? '—'}
                      </TableCell>
                      {courierPartyId === '' ? (
                        <TableCell className="text-(--text-secondary)">
                          {delivery.courierName ?? '—'}
                        </TableCell>
                      ) : null}
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {delivery.dispatchedAt === null
                          ? '—'
                          : stamp.format(new Date(delivery.dispatchedAt))}
                        {delivery.dispatchedByName === null
                          ? ''
                          : ` · ${delivery.dispatchedByName}`}
                      </TableCell>
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {delivery.settledAt === null
                          ? '—'
                          : stamp.format(new Date(delivery.settledAt))}
                      </TableCell>
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {streetTime(delivery)}
                      </TableCell>
                      {/*
                        O que ele disse que ia pagar, ao lado do que pagou —
                        F189. Estava gravado em 1 793 títulos e não aparecia em
                        tela nenhuma.
                      */}
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {delivery.expectedMethodName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <ul className="flex flex-col text-(length:--text-sm)">
                          {delivery.settlements.map((part, index) => (
                            <li key={index}>
                              {part.methodName}{' '}
                              <span className="font-(--font-mono) text-(--text-secondary)">
                                {formatMoney(money(part.amount))}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </TableCell>
                      <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                        {settledBy(delivery) === '' ? '—' : settledBy(delivery)}
                      </TableCell>
                      <TableCell className="text-right font-(--font-mono)">
                        {formatMoney(money(delivery.totalAmount))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </QueryState>

          <Pagination
            page={deliveries.data?.page ?? page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            label="Páginas dos acertos"
          />
        </TabsContent>
      </Tabs>

      {printable === null ? null : (
        <PrintDocumentDialog
          document={printable}
          description="Acerto do motoboy"
          initialPaper="a4"
          onClose={() => {
            setPrintable(null)
          }}
        />
      )}

      {cashOnly ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setCashOnly(false)
          }}
        >
          <DialogContent
            title="Tudo em dinheiro"
            description={`${selectedName} diz que todos pagaram em dinheiro. Confira o maço antes de confirmar.`}
          >
            {everyOpen.isPending ? (
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                Somando as entregas abertas do filtro…
              </p>
            ) : everyOpen.isError ? (
              <InlineAlert tone="danger">
                Não deu para somar as entregas. Volte e tente de novo.
              </InlineAlert>
            ) : (
              <p className="text-(length:--text-sm)">
                {settleable.length}{' '}
                {settleable.length === 1 ? 'entrega' : 'entregas'}:{' '}
                <strong className="font-(--font-mono)">
                  {formatMoney(settleableTotal)}
                </strong>{' '}
                entram na gaveta.
              </p>
            )}
            {tooMany ? (
              <InlineAlert tone="warning">
                O filtro tem mais entregas do que dá para acertar de uma vez.
                Estas entram agora; acerte as outras em seguida.
              </InlineAlert>
            ) : null}
            {cashSessionId === null ? (
              <InlineAlert tone="warning">
                Não há caixa aberto neste terminal: o dinheiro não tem onde
                entrar. Abra o caixa antes.
              </InlineAlert>
            ) : null}
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setCashOnly(false)
                }}
              >
                Voltar
              </Button>
              <Button
                variant="primary"
                disabled={cashSessionId === null || settleable.length === 0}
                loading={settleAllCash.isPending}
                onClick={() => {
                  settleAllCash.mutate(settleable)
                }}
              >
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {paying ? (
        <CourierPayDialog
          courierName={selectedName}
          cashSessionId={cashSessionId}
          branchId={branchId}
          onDone={(message) => {
            setPaying(false)
            setError(null)
            setNotice(message)
            void queryClient.invalidateQueries({ queryKey: ['expenses'] })
            void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
          }}
          onClose={() => {
            setPaying(false)
          }}
        />
      ) : null}

      {returning === null ? null : (
        <AuthorisationDialog
          title={`Desfazer a entrega do pedido nº ${formatDocumentNumber(returning.number, '')}`}
          description={`A mercadoria volta ao estoque e o título a receber é cancelado${returning.courierName === null ? '' : `, e ${returning.courierName} deixa de ter o que acertar`}. Um supervisor precisa autorizar.`}
          action="sales.cancel"
          targetType="sales_document"
          targetId={returning.documentId}
          onAuthorised={(authorisationId, reason) => {
            undo.mutate({
              documentId: returning.documentId,
              documentVersion: returning.documentVersion,
              authorisationId,
              reason,
            })
          }}
          onClose={() => {
            setReturning(null)
          }}
        />
      )}

      {releasing === null || releaseParty === null ? null : (
        <AuthorisationDialog
          title={`Liberar o fiado de ${releasing.delivery.customerName}`}
          description="O cliente tem título vencido. O gerente digita a senha e o pedido fica na conta do cliente; fica registrado quem liberou."
          action="finance.credit.manage"
          targetType="party"
          targetId={releaseParty}
          onAuthorised={(authorisationId) => {
            const again = releasing
            setReleasing(null)
            settle.mutate({ ...again, creditAuthorisationId: authorisationId })
          }}
          onClose={() => {
            setReleasing(null)
          }}
        />
      )}
    </Page>
  )
}

const CourierCard = ({
  name,
  detail,
  amount,
  pressed,
  onClick,
}: {
  name: string
  detail: string
  amount: ReturnType<typeof money> | undefined
  pressed: boolean
  onClick: () => void
}): ReactNode => (
  <button
    type="button"
    aria-pressed={pressed}
    onClick={onClick}
    className={cn(
      'flex items-baseline gap-2 rounded-full border px-3 py-1.5 text-left text-(length:--text-sm) transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)',
      pressed
        ? 'border-(--brand-primary) bg-(--surface-sunken)'
        : 'border-(--border-default) bg-(--surface-raised) hover:bg-(--surface-hover)'
    )}
  >
    <span className="max-w-40 truncate font-(--weight-semibold)">{name}</span>
    <span className="text-(length:--text-xs) text-(--text-muted)">
      {detail}
    </span>
    <span
      className={cn(
        'font-(--font-mono) font-(--weight-semibold) tabular-nums',
        amount !== undefined && isPositive(amount)
          ? 'text-(--text-primary)'
          : 'text-(--text-muted)'
      )}
    >
      {formatMoney(amount ?? ZERO_MONEY)}
    </span>
  </button>
)

/**
 * O pagamento do motoboy que não é fichado (F161).
 *
 * Decisão do dono: *"essa de pagar o motoboy vira valor digitado, porque eles
 * se viram"*. Não há tabela de preço por corrida: o caixa digita o que pagou,
 * e isso vira um gasto — do caixa, em dinheiro, quando há caixa aberto. O
 * motoboy fichado recebe salário na Folha e não passa por aqui.
 */
const CourierPayDialog = ({
  courierName,
  cashSessionId,
  branchId,
  onDone,
  onClose,
}: {
  courierName: string
  cashSessionId: string | null
  branchId: string | undefined
  onDone: (message: string) => void
  onClose: () => void
}): ReactNode => {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [source, setSource] = useState<'cash' | 'other'>(
    cashSessionId === null ? 'other' : 'cash'
  )
  const [error, setError] = useState<string | null>(null)

  const pay = useMutation({
    mutationFn: async () =>
      await recordExpense({
        ...(branchId === undefined ? {} : { branchId }),
        kind: 'expense',
        description:
          `Entregas — ${courierName}${note.trim() === '' ? '' : ` (${note.trim()})`}`.slice(
            0,
            200
          ),
        amount,
        spentOn: localDay(new Date()),
        paymentSource: source,
        cashSessionId: source === 'cash' ? cashSessionId : null,
      }),
    onSuccess: () => {
      onDone(
        `Pagamento de ${formatMoney(money(amount))} para ${courierName} lançado em Folha e gastos.`
      )
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível lançar o pagamento.'
      )
    },
  })

  const valid =
    amount !== '' &&
    isPositive(money(amount)) &&
    (source === 'other' || cashSessionId !== null)

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Pagar ${courierName}`}
        description="O que a loja pagou pelas corridas. Vira um gasto em Folha e gastos."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (valid) pay.mutate()
          }}
        >
          <Field label="Valor pago">
            {(field) => (
              <MoneyInput
                {...field}
                value={amount}
                onValueChange={setAmount}
                autoFocus
              />
            )}
          </Field>
          <Field label="Observação" hint="Opcional — ex.: 8 corridas.">
            {(field) => (
              <Input
                {...field}
                value={note}
                maxLength={80}
                onChange={(event) => {
                  setNote(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Saiu de onde"
            hint={
              cashSessionId === null
                ? 'Não há caixa aberto neste terminal: só dá para lançar como outro.'
                : undefined
            }
          >
            {(field) => (
              <RadioGroup
                id={field.id}
                name="courier-pay-source"
                value={source}
                onValueChange={setSource}
                options={
                  cashSessionId === null
                    ? [{ value: 'other', label: 'Outro (PIX, conta da loja)' }]
                    : [
                        { value: 'cash', label: 'Do caixa, em dinheiro' },
                        { value: 'other', label: 'Outro (PIX, conta da loja)' },
                      ]
                }
              />
            )}
          </Field>
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>
              Voltar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!valid}
              loading={pay.isPending}
            >
              Lançar pagamento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * A conta do motoboy no recorte — F203.
 *
 * O dono: *"ver o total de pedido que o motoboy levou, quanto deu, quanto
 * falta acertar, quanto acertou"*. Vem do servidor somado sobre o recorte
 * inteiro — nunca a página de 25 —, e não depende da aba.
 */
const DeliveryRecapTiles = ({
  recap,
  loading,
  error,
  who,
}: {
  recap: DeliveryRecap | undefined
  loading: boolean
  error: boolean
  who: string
}): ReactNode => {
  const shown = !loading && !error && recap !== undefined
  return (
    <section
      aria-label={`Resumo de ${who}`}
      className="grid grid-cols-2 gap-2 lg:grid-cols-4"
    >
      <StatTile
        label="Pedidos levados"
        loading={loading}
        error={error}
        value={shown ? String(recap.count) : null}
        hint={
          shown ? `Deu ${formatMoney(money(recap.totalAmount))}` : undefined
        }
      />
      <StatTile
        label="Acertado"
        loading={loading}
        error={error}
        value={
          shown ? (
            <span className="font-(--font-mono) tabular-nums text-(--success)">
              {formatMoney(money(recap.settledAmount))}
            </span>
          ) : null
        }
      />
      <StatTile
        label="Falta acertar"
        loading={loading}
        error={error}
        value={
          shown ? (
            <span
              className={cn(
                'font-(--font-mono) tabular-nums',
                isPositive(money(recap.outstandingAmount))
                  ? 'text-(--danger)'
                  : 'text-(--text-muted)'
              )}
            >
              {formatMoney(money(recap.outstandingAmount))}
            </span>
          ) : null
        }
        hint={
          shown
            ? `${String(recap.outstandingCount)} ${recap.outstandingCount === 1 ? 'pedido' : 'pedidos'}`
            : undefined
        }
      />
      <StatTile
        label="Acertado por forma"
        loading={loading}
        error={error}
        value={
          shown ? (
            recap.byMethod.length === 0 ? (
              <span className="text-(length:--text-sm) font-(--weight-normal) text-(--text-muted)">
                Nada acertado
              </span>
            ) : (
              <ul className="flex flex-col gap-0.5 text-(length:--text-sm) font-(--weight-normal)">
                {recap.byMethod.map((method) => (
                  <li
                    key={method.paymentMethodId ?? method.name}
                    className="flex justify-between gap-2"
                  >
                    <span className="truncate">{method.name}</span>
                    <span className="font-(--font-mono) tabular-nums">
                      {formatMoney(money(method.amount))}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : null
        }
      />
    </section>
  )
}
